import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';

import Icon from '../../../../shared/_TBComponents/Icon';
import Spinner from '../../../../shared/_TBComponents/Spinner';
import ExercisePreviewRow from './ExercisePreviewRow';

// Presentational only — data + transfer action owned by ProgramTemplatesModal.
// This is the step-2 ("Preview and Download") body.

// targetExerciseDatabase values for the transfer endpoint: 1 = Imported tab, 0 = Master database.
export const DB_IMPORTED = 1;
export const DB_MASTER = 0;

const TAG_FALLBACK = '#9E9E9E';

const PreviewSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 8px 32px;
`;

const TitleGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const TemplateName = styled.span`
  font-family: "Nunito Sans";
  font-size: 20px;
  font-weight: 700;
  line-height: 1.5;
  color: #1A1A1A;
`;

const MetaRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
`;

const MetaText = styled.span`
  font-family: "Nunito Sans";
  font-size: 16px;
  font-weight: 400;
  line-height: 1.5;
  color: #1A1A1A;
`;

export const TagChip = styled.span`
  font-family: "Nunito Sans";
  display: inline-flex;
  align-items: center;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 700;
  line-height: 1.5;
  white-space: nowrap;
  color: ${(props) => props.tintColor};
  background: ${(props) => props.tintBg};
`;

const PagerCard = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const PagerBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 32px;
  padding: 8px 0;
  border: 1px solid #D6D6D6;
  border-radius: 4px 4px 0 0;
  background: #FFFFFF;
  box-shadow: 0 4px 20px 0 rgba(48, 49, 51, 0.10);
  position: relative;
  z-index: 1;
`;

const PagerButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  border-radius: 4px;
  border: 2px solid ${(props) => (props.disabled ? '#BDBDBD' : '#1A1A1A')};
  background: #FFFFFF;
  cursor: ${(props) => (props.disabled ? 'default' : 'pointer')};

  &:not(:disabled):hover { opacity: 0.7; }
  &:focus-visible { outline: 2px solid #1787FB; outline-offset: 2px; }
`;

const PagerLabel = styled.span`
  font-family: "Nunito Sans";
  font-size: 18px;
  font-weight: 700;
  line-height: 1.5;
  color: #1A1A1A;
  min-width: 140px;
  text-align: center;
`;

const DayBox = styled.div`
  display: flex;
  justify-content: center;
  padding: 16px 0;
  border: 1px solid #D6D6D6;
  border-top: none;
  border-radius: 0 0 4px 4px;
  overflow: hidden;
`;

const DayColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 248px;
  max-width: 100%;
  height: 270px;
  overflow-y: auto;
`;

const Centered = styled.div`
  font-family: "Nunito Sans";
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  color: #9E9E9E;
  font-size: 14px;
  text-align: center;
  padding: 32px 16px;
`;

const EmptyDay = styled.div`
  font-family: "Nunito Sans";
  font-size: 12px;
  color: #9E9E9E;
  padding: 6px 0 8px;
`;

const DestinationSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 0 24px;
`;

const DestinationTitle = styled.span`
  font-family: "Nunito Sans";
  font-size: 16px;
  font-weight: 700;
  line-height: 1.5;
  color: #1A1A1A;
`;

const DestinationRadio = styled.label`
  font-family: "Nunito Sans";
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 16px;
  font-weight: 400;
  line-height: 1.5;
  color: #1A1A1A;
  cursor: pointer;

  input {
    width: 20px;
    height: 20px;
    margin: 2px;
    accent-color: #1787FB;
    cursor: pointer;
    &:focus-visible { outline: 2px solid #1787FB; outline-offset: 2px; }
  }
`;

// Solid chip matching the browse list: tag color as the background, text
// flipped white/dark by luminance so dark tags stay readable.
export const tagSolid = (color) => {
  const hex = /^#[0-9a-fA-F]{6}/.test(color || '') ? color : TAG_FALLBACK;
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  const yiq = (r * 299 + g * 587 + b * 114) / 1000;
  return { bg: hex, fg: yiq >= 150 ? '#1A1A1A' : '#FFFFFF' };
};

// Labels match the Manage Exercises tabs these destinations map to.
const DESTINATION_OPTIONS = [
  { value: DB_IMPORTED, label: 'Imported Exercises' },
  { value: DB_MASTER, label: 'Our Exercises' },
];

export const formatStats = (template) => {
  const weeks = template.numberOfWeeks ?? '–';
  const sessions = template.numberOfSessions ?? '–';
  return `${weeks} ${weeks === 1 ? 'Week' : 'Weeks'} | ${sessions} ${sessions === 1 ? 'Session' : 'Sessions'}`;
};

const ProgramTemplatePreview = ({
  template, weeks, isDetailLoading, destination, setDestination,
}) => {
  const [dayIndex, setDayIndex] = useState(0);

  const flatDays = useMemo(() => (
    weeks.flatMap((w) => w.days.map((d) => ({ ...d, week: w.week })))
  ), [weeks]);

  // Reset to the first day whenever a new template's detail loads.
  useEffect(() => {
    setDayIndex(0);
  }, [template && template.id]);

  if (!template) return null;

  const safeIndex = Math.min(dayIndex, Math.max(flatDays.length - 1, 0));
  const currentDay = flatDays[safeIndex];
  const exercises = (currentDay && currentDay.exercises) || [];
  const prevDisabled = safeIndex === 0;
  const nextDisabled = safeIndex >= flatDays.length - 1;

  return (
    <>
      <PreviewSection>
        <TitleGroup>
          <TemplateName>{template.programName || 'Untitled template'}</TemplateName>
          <MetaRow>
            <MetaText>{formatStats(template)}</MetaText>
            {(template.tags || []).map((tag) => {
              const tint = tagSolid(tag.color);
              return (
                <TagChip key={tag.id} tintColor={tint.fg} tintBg={tint.bg}>
                  {tag.name}
                </TagChip>
              );
            })}
          </MetaRow>
        </TitleGroup>

        <PagerCard>
          <PagerBar>
            <PagerButton
              type='button'
              aria-label='Previous day'
              disabled={prevDisabled}
              onClick={() => setDayIndex((i) => Math.max(i - 1, 0))}
            >
              <Icon icon='left-arrow' size={16} color={prevDisabled ? '#BDBDBD' : '#1A1A1A'} />
            </PagerButton>
            <PagerLabel>
              {currentDay ? `Week ${currentDay.week} - Day ${currentDay.dayInWeek}` : '—'}
            </PagerLabel>
            <PagerButton
              type='button'
              aria-label='Next day'
              disabled={nextDisabled}
              onClick={() => setDayIndex((i) => Math.min(i + 1, flatDays.length - 1))}
            >
              <Icon icon='right-arrow' size={16} color={nextDisabled ? '#BDBDBD' : '#1A1A1A'} />
            </PagerButton>
          </PagerBar>

          <DayBox>
            <DayColumn>
              {isDetailLoading && <Centered><Spinner /></Centered>}
              {!isDetailLoading && flatDays.length === 0 && (
                <Centered>No day-by-day detail to preview.</Centered>
              )}
              {!isDetailLoading && flatDays.length > 0 && (
                exercises.length === 0 ? (
                  <EmptyDay>No exercises on this day.</EmptyDay>
                ) : (
                  exercises.map((ex, i) => (
                    <ExercisePreviewRow
                      // eslint-disable-next-line react/no-array-index-key
                      key={ex.assignedId || ex.exerciseId || `${safeIndex}-${i}`}
                      exercise={ex}
                    />
                  ))
                )
              )}
            </DayColumn>
          </DayBox>
        </PagerCard>
      </PreviewSection>

      <DestinationSection role='radiogroup' aria-label='Where do you want your exercises to go?'>
        <DestinationTitle>Where do you want your exercises to go?</DestinationTitle>
        {DESTINATION_OPTIONS.map((opt) => (
          <DestinationRadio key={opt.value}>
            <input
              type='radio'
              name='template-destination'
              value={opt.value}
              checked={destination === opt.value}
              onChange={() => setDestination(opt.value)}
            />
            {opt.label}
          </DestinationRadio>
        ))}
      </DestinationSection>
    </>
  );
};

ProgramTemplatePreview.propTypes = {
  template: PropTypes.shape({
    id: PropTypes.number,
    programName: PropTypes.string,
    numberOfWeeks: PropTypes.number,
    numberOfSessions: PropTypes.number,
    tags: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
      color: PropTypes.string,
    })),
  }),
  weeks: PropTypes.arrayOf(PropTypes.shape({
    week: PropTypes.number,
    days: PropTypes.arrayOf(PropTypes.shape({})),
  })),
  isDetailLoading: PropTypes.bool,
  destination: PropTypes.number.isRequired,
  setDestination: PropTypes.func.isRequired,
};

ProgramTemplatePreview.defaultProps = {
  template: null,
  weeks: [],
  isDetailLoading: false,
};

export default ProgramTemplatePreview;
